import { INavigation, INavigationElement } from 'global';
import {
  INavigationData,
  NavigationItem,
  TrustLinkIf,
} from 'interfaces/navigation-data';
import { Gtm, gtm } from 'tracking/gtm';

export const NavigationType = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
} as const;

export const TargetGroup = {
  NEW_CUSTOMER: 'New Customer',
  EXISTING_CUSTOMER: 'Existing Customer',
} as const;

export const PetType = {
  DOG: 'Dog',
  CAT: 'Cat',
} as const;

export const commonTrustbar: TrustLinkIf[] = [
  {
    title: 'Beratung',
    url: '/pages/unsere-futterberatung-online-und-telefonisch',
    target: '_self',
    rel: '',
  },
  {
    title: 'Magazin',
    url: '/magazin',
    target: '_self',
    rel: '',
  },
  {
    title: 'FAQ',
    url: 'https://help.petsdeli.de/hc/de',
    target: '_self',
    rel: '',
  },
  {
    title: 'Bonusprogramm',
    url: '/pages/bonusprogramm',
    target: '_self',
    rel: '',
  },
];

export const footer = (): INavigationData['footer'] => {
  return {
    usefulInformation: [
      {
        title: 'Über Uns',
        url: '/pages/uber-uns',
      },
      {
        title: 'Was ist gesundes Tierfutter?',
        url: '/magazin/hunde/hunde-ernaehrung/was-ist-gesundes-tierfutter',
      },
      {
        title: 'Warum Premium-Tiernahrung?',
        as: '/pages/premium',
        url: '/pages/[handle]',
      },
      {
        title: 'Umfütterung',
        url: '/magazin/hunde/hunde-ernaehrung/umfutterung-von-hunden',
      },
      {
        title: 'Unsere Stores',
        as: '/pages/unsere-stores',
        url: '/pages/[handle]',
      },
    ],
    goodForYou: [
      {
        title: 'Freund*innen werben',
        as: '/pages/freund-innen-werben',
        url: '/pages/[handle]',
      },
      {
        title: 'Markenbotschafter*in werden',
        url: '/pages/markenbotschafter-in-werden',
        attrs: {
          onClick: () =>
            gtm({
              group: Gtm.GroupName.Component,
              name: Gtm.ComponentEventName.Click,
              data: {
                label: 'AmassadorProgramm-Footer',
              },
            }),
        },
      },
      {
        title: 'Unser Spar-Abo',
        as: '/pages/unser-spar-abo',
        url: '/pages/[handle]',
      },
    ],
    help: [
      {
        title: 'Häufige Fragen',
        url: 'https://help.petsdeli.de/hc/de',
        attrs: {
          rel: 'noopener',
        },
        target: '_blank',
      },
      {
        title: 'Versand',
        url: '/pages/[handle]',
        as: '/pages/versand',
      },
      {
        title: 'Pets Deli im Handel',
        url: '/pages/[handle]',
        as: '/pages/haendlersuche',
      },
      {
        title: 'Züchter*innenprogramm',
        url: '/pages/[handle]',
        as: '/pages/zuchter-innen-programm',
      },
    ],
    legal: [
      {
        title: 'Karriere',
        url: '/pages/karriere',
      },
      {
        title: 'Impressum',
        url: '/pages/[handle]',
        as: '/pages/impressum',
      },
      {
        title: 'Datenschutz',
        url: '/pages/[handle]',
        as: '/pages/datenschutz',
      },
      {
        title: 'AGB und Widerruf',
        url: '/pages/[handle]',
        as: '/pages/agb-und-widerruf',
      },
    ],
  };
};

/**
 * Filters navigation elements based on type, customer type, and pet type
 */
const filterNavigationElement = (
  elements: INavigationElement[],
  type: (typeof NavigationType)[keyof typeof NavigationType],
  targetGroup: (typeof TargetGroup)[keyof typeof TargetGroup],
  petType: (typeof PetType)[keyof typeof PetType]
): INavigationElement | undefined => {
  return elements.find(
    (element) =>
      element?.fields?.type === type &&
      element?.fields?.targetGroup === targetGroup &&
      element?.fields?.petType === petType
  );
};

/**
 * Transforms a Contentful navigation structure into a two-dimensional array of NavigationItem objects.
 */
const transformNavigationStructure = (
  structure: Record<string, unknown>
): NavigationItem[][] => {
  if (!structure) return [];

  // If it's already an array, return it as-is
  if (Array.isArray(structure)) {
    return structure as NavigationItem[][];
  }

  return [];
};

/**
 * Creates navigation data from Contentful elements
 */
export const createNavigationFromContentful = ({
  elements,
  isExistingCustomer,
  dogTitle,
  catTitle,
  homeDogUrl,
  homeCatUrl,
}: {
  elements: INavigationElement[];
  isExistingCustomer: boolean;
  dogTitle: string;
  catTitle: string;
  homeDogUrl: string;
  homeCatUrl: string;
}): INavigationData => {
  const targetGroup = isExistingCustomer
    ? TargetGroup.EXISTING_CUSTOMER
    : TargetGroup.NEW_CUSTOMER;

  // Find mobile navigation for dogs and cats
  const mobileDogs = filterNavigationElement(
    elements,
    NavigationType.MOBILE,
    targetGroup,
    PetType.DOG
  );
  const mobileCats = filterNavigationElement(
    elements,
    NavigationType.MOBILE,
    targetGroup,
    PetType.CAT
  );

  // Find desktop navigation for dogs and cats
  const desktopDogs = filterNavigationElement(
    elements,
    NavigationType.DESKTOP,
    targetGroup,
    PetType.DOG
  );
  const desktopCats = filterNavigationElement(
    elements,
    NavigationType.DESKTOP,
    targetGroup,
    PetType.CAT
  );

  return {
    mobile: {
      dogs: mobileDogs?.fields.structure
        ? transformNavigationStructure(mobileDogs.fields.structure)
        : [],
      cats: mobileCats?.fields.structure
        ? transformNavigationStructure(mobileCats.fields.structure)
        : [],
    },
    desktop: [
      ...(desktopDogs?.fields.structure
        ? [
            {
              title: dogTitle,
              context: 'dogs' as const,
              url: homeDogUrl,
              items: transformNavigationStructure(
                desktopDogs.fields.structure
              ).flat() as NavigationItem[],
            },
          ]
        : []),
      ...(desktopCats?.fields.structure
        ? [
            {
              title: catTitle,
              context: 'cats' as const,
              url: homeCatUrl,
              items: transformNavigationStructure(
                desktopCats.fields.structure
              ).flat() as NavigationItem[],
            },
          ]
        : []),
    ],
    footer: footer(),
    trustbar: commonTrustbar,
  };
};

/**
 * Type guard to check if an object is a valid INavigation
 */
export const isNavigation = (obj: unknown): obj is INavigation => {
  if (!obj || typeof obj !== 'object') return false;

  const candidate = obj as Record<string, unknown>;

  if (!('fields' in candidate) || !candidate.fields) return false;

  if (typeof candidate.fields !== 'object' || candidate.fields === null)
    return false;

  const fields = candidate.fields as Record<string, unknown>;

  return 'element' in fields;
};
